import api from './api';

export const orderService = {
  // Obtener todos los pedidos con filtros
  async getOrders(params = {}) {
    try {
      const response = await api.get('/api/orders', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al obtener pedidos',
      };
    }
  },

  // Obtener un pedido por ID
  async getOrder(id) {
    try {
      const response = await api.get(`/api/orders/${id}`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al obtener pedido',
      };
    }
  },

  // Crear pedido subiendo archivo (Paso 1: Estado CREATED)
  async createOrder(file, providerId = null) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (providerId) {
        formData.append('providerId', providerId);
      }

      const response = await api.post('/api/orders', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al crear pedido',
      };
    }
  },

  // Asignar proveedor y detalles (Paso 2: CREATED -> PENDING_SIGNATURE)
  async updateOrderProvider(id, orderData) {
    try {
      const response = await api.put(`/api/orders/${id}/provider`, orderData);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al actualizar pedido',
      };
    }
  },

  // Aprobar pedido (Paso 3: PENDING_SIGNATURE -> PENDING_TRANSFER)
  async approveOrder(id) {
    try {
      const response = await api.put(`/api/orders/${id}/approve`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al aprobar pedido',
      };
    }
  },

  // Definir método de pago (Paso 4: PENDING_TRANSFER -> PENDING_PAYMENT o COMPLETED)
  async updatePaymentMethod(id, paymentMethodData) {
    try {
      // Si paymentMethodData es string, mantener compatibilidad hacia atrás
      const requestData = typeof paymentMethodData === 'string' 
        ? { paymentMethod: paymentMethodData }
        : paymentMethodData;

      const response = await api.put(`/api/orders/${id}/payment-method`, requestData);
      
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al actualizar método de pago',
      };
    }
  },

  // Marcar como pagado (Paso 5: PENDING_PAYMENT -> COMPLETED)
  async markOrderAsPaid(id, paymentData) {
    try {
      const response = await api.put(`/api/orders/${id}/mark-paid`, paymentData);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al marcar como pagado',
      };
    }
  },

  // Cancelar pedido (Cualquier estado -> CANCELLED)
  async cancelOrder(id, reason) {
    try {
      const response = await api.put(`/api/orders/${id}/cancel`, { reason });
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al cancelar pedido',
      };
    }
  },

  // Añadir archivo adicional a un pedido
  async addFileToOrder(id, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/api/orders/${id}/files`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al añadir archivo',
      };
    }
  },

  // Eliminar archivo de un pedido
  async deleteFile(fileId) {
    try {
      const response = await api.delete(`/api/orders/files/${fileId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Error al eliminar archivo',
      };
    }
  },

  // Métodos auxiliares para el flujo de trabajo
  getStatusLabel(status) {
    const statusLabels = {
      CREATED: 'Creado',
      PENDING_SIGNATURE: 'Pendiente de Firma',
      PENDING_TRANSFER: 'Pendiente de Transferencia',
      PENDING_PAYMENT: 'Pendiente de Pago',
      COMPLETED: 'Completado',
      CANCELLED: 'Cancelado',
    };
    return statusLabels[status] || status;
  },

  getStatusColor(status) {
    const statusColors = {
      CREATED: 'primary',
      PENDING_SIGNATURE: 'warning',
      PENDING_TRANSFER: 'info',
      PENDING_PAYMENT: 'warning',
      COMPLETED: 'success',
      CANCELLED: 'error',
    };
    return statusColors[status] || 'default';
  },

  getAvailableTransitions(status) {
    const transitions = {
      CREATED: ['PENDING_SIGNATURE', 'CANCELLED'],
      PENDING_SIGNATURE: ['PENDING_TRANSFER', 'CANCELLED'],
      PENDING_TRANSFER: ['PENDING_PAYMENT', 'COMPLETED', 'CANCELLED'], // Puede ir directo a COMPLETED con pago inmediato
      PENDING_PAYMENT: ['COMPLETED', 'CANCELLED'],
      COMPLETED: [],
      CANCELLED: [],
    };
    return transitions[status] || [];
  },

  // Enviar email de notificación
  async sendNotificationEmail(emailPayload, credentials = {}) {
    try {
      const response = await fetch('https://app.confirma.es/ws_API.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user': process.env.REACT_APP_EMAIL_USER,
          'password': process.env.REACT_APP_EMAIL_PASSWORD
        },
        body: JSON.stringify(emailPayload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Error al enviar email de notificación',
      };
    }
  },
}; 